'use strict';

(function () {
  class ChProductPageEnhancements {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.section;
      this.purchaseRow = root.querySelector(`#ProductPurchaseRow-${this.sectionId}`);
      this.productForm = root.querySelector(`#product-form-${this.sectionId}`);
      this.submitButton = root.querySelector(`#ProductSubmitButton-${this.sectionId}`);
      this.priceRoot = root.querySelector(`#price-${this.sectionId}`);
      this.floatingCart = root.querySelector('[data-product-floating-cart]');
      this.floatingName = root.querySelector('[data-product-floating-name]');
      this.floatingCurrent = root.querySelector('[data-product-floating-current]');
      this.floatingCompare = root.querySelector('[data-product-floating-compare]');
      this.floatingSubmit = root.querySelector('[data-product-floating-submit]');
      this.floatingClose = root.querySelector('[data-product-floating-close]');
      this.floatingImage = root.querySelector('.prod-floating__img');
      this.modal = root.querySelector('[data-product-cart-modal]');
      this.modalBg = root.querySelector('[data-product-cart-modal-bg]');
      this.modalName = root.querySelector('[data-product-cart-modal-name]');
      this.modalCurrent = root.querySelector('[data-product-cart-modal-current]');
      this.modalCompare = root.querySelector('[data-product-cart-modal-compare]');
      this.modalImage = root.querySelector('.modal-cart__img');
      this.modalCloseButtons = root.querySelectorAll('[data-product-cart-modal-close]');
      this.thumbnailSlider = root.querySelector(`#GalleryThumbnails-${this.sectionId}`);
      this.dismissed = false;
      this.hideTimer = null;
      this.isBound = false;
      this.currentVariantId = this.getCurrentVariantId();
    }

    init() {
      if (
        !this.purchaseRow ||
        !this.productForm ||
        !this.submitButton ||
        !this.floatingCart ||
        this.isBound
      ) {
        return;
      }

      this.isBound = true;
      this.syncFromDom();
      this.updateFloatingOffset();
      this.bindEvents();
      this.bindThumbnailRail();
      this.handleScroll();
      this.observeDomChanges();
    }

    bindEvents() {
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleFloatingSubmit = this.handleFloatingSubmit.bind(this);
      this.closeFloating = this.closeFloating.bind(this);
      this.closeModal = this.closeModal.bind(this);

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });

      if (this.floatingSubmit) {
        this.floatingSubmit.addEventListener('click', this.handleFloatingSubmit);
      }

      if (this.floatingClose) {
        this.floatingClose.addEventListener('click', this.closeFloating);
      }

      this.modalCloseButtons.forEach((button) => {
        button.addEventListener('click', this.closeModal);
      });

      if (this.modalBg) {
        this.modalBg.addEventListener('click', this.closeModal);
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event?.data || String(event.data.sectionId) !== String(this.sectionId)) {
            return;
          }

          window.setTimeout(() => {
            this.currentVariantId = this.getCurrentVariantId();
            this.syncFromDom();
            this.handleScroll();
          }, 80);
        });

        this.cartUpdateUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source !== 'product-form') return;

          const eventVariantId = String(event?.variantId || '');
          if (eventVariantId && eventVariantId !== this.getCurrentVariantId()) return;

          this.syncFromDom();
          this.openModal();
        });
      }
    }

    observeDomChanges() {
      this.domObserver = new MutationObserver(() => {
        this.currentVariantId = this.getCurrentVariantId();
        this.syncFromDom();
        this.bindThumbnailRail();
      });

      if (this.priceRoot) {
        this.domObserver.observe(this.priceRoot, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      const mediaWrapper = this.root.querySelector('.product__media-wrapper');
      if (mediaWrapper) {
        this.domObserver.observe(mediaWrapper, {
          attributes: true,
          attributeFilter: ['class', 'src', 'srcset'],
          childList: true,
          subtree: true,
        });
      }

      this.submitObserver = new MutationObserver(() => {
        this.syncButtonState();
      });

      this.submitObserver.observe(this.submitButton, {
        attributes: true,
        attributeFilter: ['class', 'disabled', 'aria-disabled'],
        childList: true,
        subtree: true,
      });
    }

    bindThumbnailRail() {
      if (!this.thumbnailSlider || this.thumbnailSlider.dataset.chRailBound === 'true') {
        return;
      }

      const list = this.thumbnailSlider.querySelector('.thumbnail-list');
      const prevButton = this.thumbnailSlider.querySelector('.slider-button--prev');
      const nextButton = this.thumbnailSlider.querySelector('.slider-button--next');

      if (!list || !prevButton || !nextButton) {
        return;
      }

      const scrollByStep = (direction) => {
        if (!window.matchMedia('(min-width: 990px)').matches) {
          return;
        }

        const firstItem = list.querySelector('.thumbnail-list__item');
        const step = firstItem ? firstItem.getBoundingClientRect().height + 12 : 120;
        list.scrollBy({
          top: step * direction,
          behavior: 'smooth',
        });
      };

      prevButton.addEventListener('click', (event) => {
        if (!window.matchMedia('(min-width: 990px)').matches) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        scrollByStep(-1);
      });

      nextButton.addEventListener('click', (event) => {
        if (!window.matchMedia('(min-width: 990px)').matches) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        scrollByStep(1);
      });

      this.thumbnailSlider.dataset.chRailBound = 'true';
    }

    getCurrentVariantId() {
      return String(this.root.querySelector('.product-variant-id')?.value || '');
    }

    getCurrentTitle() {
      return this.root.querySelector('.product__title h1')?.textContent.trim() || '';
    }

    getPriceData() {
      if (!this.priceRoot) {
        return { current: '', compare: '' };
      }

      const sale = this.priceRoot.querySelector('.price-item--sale');
      const regularItems = [...this.priceRoot.querySelectorAll('.price-item--regular')];
      const fallback = this.priceRoot.querySelector('.price-item');
      const currentEl = sale || regularItems[regularItems.length - 1] || fallback;
      const compareEl = sale && regularItems.length ? regularItems[0] : null;

      return {
        current: currentEl ? currentEl.textContent.trim() : '',
        compare: compareEl ? compareEl.textContent.trim() : '',
      };
    }

    getActiveImageData() {
      const activeImage =
        this.root.querySelector('.product__media-item.is-active img') ||
        this.root.querySelector('.product__media-wrapper img');

      if (!activeImage) {
        return { src: '', alt: '' };
      }

      return {
        src: activeImage.currentSrc || activeImage.src || '',
        alt: activeImage.alt || this.getCurrentTitle(),
      };
    }

    syncFromDom() {
      const title = this.getCurrentTitle();
      const price = this.getPriceData();
      const image = this.getActiveImageData();

      if (this.floatingName && title) {
        this.floatingName.textContent = title;
      }

      if (this.modalName && title) {
        this.modalName.textContent = title;
      }

      if (this.floatingCurrent) {
        this.floatingCurrent.textContent = price.current;
      }

      if (this.modalCurrent) {
        this.modalCurrent.textContent = price.current;
      }

      this.toggleComparePrice(this.floatingCompare, price.compare);
      this.toggleComparePrice(this.modalCompare, price.compare);
      this.syncImage(this.floatingImage, image);
      this.syncImage(this.modalImage, image);
      this.syncButtonState();
    }

    syncButtonState() {
      if (!this.floatingSubmit || !this.submitButton) return;

      const buttonText = this.submitButton.textContent.trim();
      this.floatingSubmit.textContent = buttonText;
      this.floatingSubmit.disabled = this.submitButton.disabled;
      this.floatingSubmit.setAttribute('aria-disabled', this.submitButton.disabled ? 'true' : 'false');
    }

    toggleComparePrice(element, value) {
      if (!element) return;

      if (value) {
        element.textContent = value;
        element.hidden = false;
      } else {
        element.textContent = '';
        element.hidden = true;
      }
    }

    syncImage(element, image) {
      if (!element || element.tagName !== 'IMG' || !image.src) return;
      element.src = image.src;
      element.alt = image.alt;
    }

    isDesktop() {
      return window.matchMedia('(min-width: 990px)').matches;
    }

    handleResize() {
      this.updateFloatingOffset();

      if (!this.isDesktop()) {
        this.hideFloating(true);
      } else {
        this.handleScroll();
      }
    }

    updateFloatingOffset() {
      if (!this.floatingCart) return;

      const announcement = document.querySelector('.announcement');
      this.floatingCart.style.top = announcement ? `${announcement.offsetHeight}px` : '0px';
    }

    handleScroll() {
      if (!this.isDesktop()) {
        this.hideFloating(true);
        return;
      }

      const rect = this.purchaseRow.getBoundingClientRect();
      const topOffset = parseFloat(this.floatingCart?.style.top || '0') + 12;
      const isInView = rect.top < window.innerHeight && rect.bottom > topOffset;
      const hasPassed = rect.bottom <= topOffset;

      if (isInView) {
        this.dismissed = false;
      }

      if (hasPassed && !this.dismissed) {
        this.showFloating();
      } else {
        this.hideFloating();
      }
    }

    showFloating() {
      if (!this.floatingCart) {
        return;
      }

      window.clearTimeout(this.hideTimer);
      this.floatingCart.hidden = false;
      this.floatingCart.setAttribute('aria-hidden', 'false');
      this.floatingCart.classList.remove('hide');

      window.requestAnimationFrame(() => {
        this.floatingCart.classList.add('show');
      });
    }

    hideFloating(forceInstant) {
      if (!this.floatingCart || this.floatingCart.hidden) return;

      this.floatingCart.classList.remove('show');

      if (forceInstant) {
        this.floatingCart.classList.remove('hide');
        this.floatingCart.hidden = true;
        this.floatingCart.setAttribute('aria-hidden', 'true');
        return;
      }

      this.floatingCart.classList.add('hide');
      window.clearTimeout(this.hideTimer);
      this.hideTimer = window.setTimeout(() => {
        if (!this.floatingCart.classList.contains('show')) {
          this.floatingCart.hidden = true;
          this.floatingCart.setAttribute('aria-hidden', 'true');
          this.floatingCart.classList.remove('hide');
        }
      }, 320);
    }

    closeFloating() {
      this.dismissed = true;
      this.hideFloating();
    }

    handleFloatingSubmit() {
      if (!this.productForm || !this.submitButton || this.submitButton.disabled) {
        return;
      }

      this.purchaseRow.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      window.setTimeout(() => {
        if (typeof this.productForm.requestSubmit === 'function') {
          this.productForm.requestSubmit(this.submitButton);
        } else {
          this.submitButton.click();
        }
      }, 350);
    }

    openModal() {
      if (!this.modal || !this.modalBg) return;

      this.modal.hidden = false;
      this.modalBg.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');

      window.requestAnimationFrame(() => {
        this.modal.classList.add('show');
        this.modalBg.classList.add('show');
      });
    }

    closeModal() {
      if (!this.modal || !this.modalBg) return;

      this.modal.classList.remove('show');
      this.modalBg.classList.remove('show');
      this.modal.setAttribute('aria-hidden', 'true');

      window.setTimeout(() => {
        if (!this.modal.classList.contains('show')) {
          this.modal.hidden = true;
          this.modalBg.hidden = true;
        }
      }, 320);
    }
  }

  function initProductPageEnhancements() {
    document.querySelectorAll('product-info[data-product-page-root]').forEach((root) => {
      if (root.chProductPageEnhancements) return;
      root.chProductPageEnhancements = new ChProductPageEnhancements(root);
      root.chProductPageEnhancements.init();
    });
  }

  document.addEventListener('DOMContentLoaded', initProductPageEnhancements);
  document.addEventListener('shopify:section:load', initProductPageEnhancements);
})();
